import React from 'react';
import useDataSourceContext from '../useDataSourceContext';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '../../ConfirmDialog';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Radio from '@material-ui/core/Radio';
import ContentDrawer from '../../ContentDrawer';
import DefaultViewContainer from './DefaultViewContainer';
import DraggableContainer from './DraggableContainer';
import DroppableContainer from './DroppableContainer';
import Icon from '../../Icon';
import translations from './translations';

const ViewSelection = () => {
  const confirm = useConfirm();
  const intl = useIntl();
  const {
    showViewsSelection,
    setShowViewsSelection,
    views: viewsModule = {},
  } = useDataSourceContext();
  const {
    defaultView,
    fixedViews = [],
    getAvailableViews,
    getActiveView,
    setActiveView,
    repositionView,
    deleteView,
    setDefaultView,
  } = viewsModule;
  const activeView = getActiveView && getActiveView();
  const availableViews = getAvailableViews && getAvailableViews();
  const isFixedView = (fixedViews ?? []).some(view => view.key === activeView?.key);
  const hasDefaultView = (availableViews ?? []).some(view => view.isDefault);
  const showSetDefaultAction =
    activeView?.type !== 'user' &&
    !activeView?.isDefault &&
    !isFixedView &&
    (activeView?.key !== 'default' || (activeView?.key === 'default' && hasDefaultView));

  /**
   * Handle the drag event of the views.
   * Set the new views based on their new positions.
   * @param {Object} result
   */
  const handleDragEnd = async result => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    repositionView(source.index, destination.index);
  };

  /**
   * Handle deleting a view.
   * @param {Object} view
   */
  const handleDelete = view => async () => {
    const confirmResponse = await confirm({
      title: intl.formatMessage(translations.confirmDeleteView.title),
      description: intl.formatMessage(translations.confirmDeleteView.description),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.confirmDeleteView.buttons.cancel),
          type: 'cancel',
          variant: 'outlined',
          style: {
            marginRight: 'auto',
          },
        },
        {
          label: intl.formatMessage(translations.confirmDeleteView.buttons.delete),
          response: 'delete',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

    if (confirmResponse === 'delete') {
      deleteView(view.key);
    }
  };

  /**
   * Handle click view selection.
   * Set the selected view selection.
   * @param {Object} view
   */
  const handleClickViewSelection = view => () => {
    if (view.key !== defaultView?.key) {
      setActiveView(view?.key);
      setShowViewsSelection(false);
    }
  };

  /**
   * Handle changing the active view.
   * @param {Event} event
   */
  const handleChangeActiveView = event => {
    setActiveView(event.target.value);
    setShowViewsSelection(false);
  };

  return (
    <ContentDrawer
      isOpen={showViewsSelection || false}
      handleClose={() => setShowViewsSelection(false)}
      title={intl.formatMessage(translations.title)}
      drawerWidth="400px"
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <DroppableContainer>
          <DefaultViewContainer>
            <div>
              <Radio
                checked={activeView && activeView.key === defaultView?.key}
                onChange={handleChangeActiveView}
                value={defaultView?.key}
                name="view-item"
                color="primary"
                inputProps={{ 'aria-label': defaultView?.label }}
              />
            </div>
            <Box flex={1}>{defaultView?.label}</Box>
          </DefaultViewContainer>
          {fixedViews.map(fixedView => (
            <DefaultViewContainer>
              <div>
                <Radio
                  checked={activeView.key === fixedView.key}
                  onChange={handleChangeActiveView}
                  value={fixedView.key}
                  name="view-item"
                  color="primary"
                  inputProps={{ 'aria-label': fixedView.label }}
                />
              </div>
              <Box flex={1}>{fixedView.label}</Box>
            </DefaultViewContainer>
          ))}
          <Droppable droppableId="available">
            {provided => (
              <Box flex={1} ref={provided.innerRef}>
                {availableViews.map((view, idx) => (
                  <Draggable key={view.key} draggableId={view.key} index={idx}>
                    {(dragProvided, snapshot) => (
                      <DraggableContainer
                        ref={dragProvided.innerRef}
                        isDragging={snapshot.isDragging}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <div>
                          <Radio
                            checked={activeView && activeView.key === view.key}
                            onChange={handleChangeActiveView}
                            value={view.key}
                            name="view-item"
                            color="primary"
                            inputProps={{ 'aria-label': view.name }}
                          />
                        </div>
                        <Box flex={1} onClick={handleClickViewSelection(view)}>
                          {view.label}
                        </Box>
                        <div>
                          {view.key !== defaultView?.label && (
                            <IconButton size="small" onClick={handleDelete(view)}>
                              <Icon icon={['fal', 'trash-alt']} style={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </div>
                      </DraggableContainer>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DroppableContainer>
      </DragDropContext>
      {showSetDefaultAction && (
        <Box mt={2}>
          <Button color="primary" variant="outlined" onClick={() => setDefaultView(activeView)}>
            <FormattedMessage {...translations.actions.setAsDefault} />
          </Button>
        </Box>
      )}
    </ContentDrawer>
  );
};

export default ViewSelection;
